import { useTranslation } from 'react-i18next';
import { Loader2, RotateCcw, Scissors, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { CROP_AXES, FULL_CROP, isFullCrop, type CropBox } from '../../../components/crag/crop-box';

interface CropPanelProps {
  crop: CropBox;
  onChange: (crop: CropBox) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}

/**
 * The three ranges of the crop box, as a share of the model on each axis.
 * The viewer above hides what falls outside as the thumbs move; nothing is
 * cut until "save".
 */
export default function CropPanel({ crop, onChange, onSave, onCancel, saving }: CropPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <p className="text-sm text-muted-foreground">{t('cragModel.crop.hint')}</p>
      <div className="grid gap-4 sm:grid-cols-3">
        {CROP_AXES.map((axis) => (
          <div key={axis} className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label id={`crop-${axis}`}>{t(`cragModel.crop.axes.${axis}`)}</Label>
              <span className="text-xs tabular-nums text-muted-foreground">
                {Math.round(crop[axis][0] * 100)}–{Math.round(crop[axis][1] * 100)}%
              </span>
            </div>
            <Slider
              aria-labelledby={`crop-${axis}`}
              min={0}
              max={100}
              step={1}
              minStepsBetweenThumbs={2}
              value={[crop[axis][0] * 100, crop[axis][1] * 100]}
              disabled={saving}
              onValueChange={([low, high]) => onChange({ ...crop, [axis]: [low / 100, high / 100] })}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t(`cragModel.crop.ends.${axis}.low`)}</span>
              <span>{t(`cragModel.crop.ends.${axis}.high`)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" className="gap-2" disabled={saving || isFullCrop(crop)} onClick={onSave}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />}
          {t('cragModel.crop.save')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          disabled={saving || isFullCrop(crop)}
          onClick={() => onChange(FULL_CROP)}
        >
          <RotateCcw className="h-4 w-4" />
          {t('cragModel.crop.reset')}
        </Button>
        <Button type="button" variant="ghost" className="gap-2" disabled={saving} onClick={onCancel}>
          <X className="h-4 w-4" />
          {t('cragModel.crop.cancel')}
        </Button>
      </div>
    </div>
  );
}
